import React, { useEffect, useState } from "react";

import { useGetAllSessions } from "@/api/chat/get-all-chat-sessions";
import ChatUser from "@/components/conversations/chat-user/chat-user";
import { Card } from "@/components/ui/card";
import useUpdateQueryParams from "@/hooks/use-update-query-params";
import { useSearchParams } from "react-router";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import NoDataFound from "@/components/layout/no-data-found";
import { useGetFilteredLogs } from "@/api/chat/get-chat-logs-by-status";

interface ChatUserListProps {
    className?: string;
}

interface ChatSession {
    session_id: string;
    last_message: string;
    session_created_at: string;
    location?: string;
    response?: string;
}

const ChatUserList: React.FC<ChatUserListProps> = ({ className }) => {
    const updateQueryParams = useUpdateQueryParams();
    const [searchParams] = useSearchParams();

    const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);

    React.useEffect(() => {
        if (!searchParams.has("chat_id") && chatSessions?.length > 0) {
            updateQueryParams({ chat_id: chatSessions[0]?.session_id });
        }
    }, [searchParams, updateQueryParams, chatSessions]);

    const { data: sessions, isLoading } = useGetAllSessions({
        enabled: searchParams.get("type") === "all" || searchParams.get("type") === "active",
        status: searchParams.get("type") === "all" ? null : searchParams.get("type") === "liked" ? 1 : 2,
    });

    const { data: filteredChats } = useGetFilteredLogs({
        enabled: searchParams.get("type") === "liked" || searchParams.get("type") === "disliked",
        status: searchParams.get("type") === "liked" ? 1 : 2,
    });

    useEffect(() => {
        setChatSessions(sessions?.data);
    }, [sessions]);

    // if (isLoading) {
    //     return (
    //         <Card className={cn("h-auto overflow-y-auto scroll-smooth border flex flex-col items-center justify-center")}>
    //             <img src={"/assets/images/others/loading.gif"} height={128} width={128} alt="loading" />
    //             <h3 className=" animate-pulse text-base font-semibold text-gray-700">{"Please wait while we load chat sessions."}</h3>
    //         </Card>
    //     );
    // }

    return (
        <Card className={cn("h-auto lg:h-[calc(100vh-80px)] overflow-y-auto scroll-smooth border", className)}>
            <div className="z-10 sticky top-0 flex items-center justify-between rounded-t-xl border-b bg-white p-4">
                <h6 className="font-semibold">Chat List</h6>
            </div>
            <div className="flex p-2 md:p-4 lg:flex-col justify-center">
                {sessions?.data?.length === 0 && (
                    // <div>
                    //     <h3 className="text-center font-semibold text-slate-700">No Conversations</h3>
                    // </div>
                    <NoDataFound message="No Chat Session Found" />
                )}
                {isLoading &&
                    [1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => (
                        <div className="flex items-center space-x-4 my-4" key={item}>
                            <Skeleton className="h-12 w-12 rounded-full" />
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-[250px]" />
                                <Skeleton className="h-4 w-[200px]" />
                            </div>
                        </div>
                    ))}
                {(searchParams.get("type") === "all" || searchParams.get("type") === "active") &&
                    sessions?.data?.length > 0 &&
                    sessions?.data?.map((session: ChatSession) => (
                        <ChatUser
                            key={session?.session_id}
                            name={session?.session_id}
                            lastMessage={session.last_message}
                            active={searchParams.get("chat_id") === session?.session_id}
                            id={session?.session_id}
                            date={session?.session_created_at}
                            location={session?.location}
                        />
                    ))}

                {(searchParams.get("type") === "liked" || searchParams.get("type") === "disliked") &&
                    filteredChats?.data?.length > 0 &&
                    filteredChats?.data?.map((session: ChatSession) => (
                        <ChatUser
                            key={session?.session_id}
                            name={session?.session_id}
                            lastMessage={session.response ?? ""}
                            active={searchParams.get("chat_id") === session?.session_id}
                            id={session?.session_id}
                            date={session?.session_created_at}
                            location={session?.location}
                        />
                    ))}
            </div>
        </Card>
    );
};

export default ChatUserList;
